import {
    Button,
    Heading,
    Input,
    List,
    ListIcon,
    ListItem,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@chakra-ui/react';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { CheckmarkCircleIcon } from '../../components/icons';
import Modal from '../../components/modal';
import Text from '../../components/text';
import SwalService from '../../services/swal';

interface IUnverifiedResponseModalProps {
    isOpen: boolean;
    onClose: () => void;
    token: string;
    bookRegionId: string;
    internalId: string;
}

export default function UnverifiedResponseModal(
    props: IUnverifiedResponseModalProps
) {
    const { isOpen, onClose, bookRegionId, internalId, token } = props;

    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const handleChangeEmailInput = (e: any) => setEmail(e.target.value);
    const handleChangeNameInput = (e: any) => setName(e.target.value);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setName('');
            setEmail('');
        }
    }, [isOpen]);

    const handleSubmitButton = async () => {

        if (!email || !name)
            return window.alert('Please enter a name and email');

        setIsLoading(true);
        const response = await fetch(
            `${process.env.REACT_APP_API_BASE || ''}/v1/support`,
            {
                method: 'POST',
                headers: {
                    Authorization: token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name,
                    email,
                    bookRegionId,
                    internalId,
                }),
            }
        );
        console.log(response.status);
        if (response.status === 200) {
            SwalService.unverifiedBookLoginTicketCreated();
        } else {
            SwalService.unverifiedBookLoginTicketError();
        }
        //onClose
        setIsLoading(false);
    }

    // debounce such that this function only can trigger once every 300ms
    // https://ellenaua.medium.com/throttle-debounce-behavior-lodash-6bcae1494e03
    // has good visualizations of the leading/trailing logic
    const handleSubmitButtonClick = useCallback(
        _.debounce(
            handleSubmitButton,
            300,
            { leading: true, trailing: false, maxWait: 300 }
        ),
        [email, name, token, bookRegionId, internalId]
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} autoFocus={false}>
            <ModalOverlay />
            <form>
                <ModalContent>
                    <ModalHeader>
                        We are having trouble linking your account
                    </ModalHeader>
                    <ModalBody>
                        <Heading textAlign="center" mb="8px" margin="6px">
                            Please make sure you have
                        </Heading>
                        <List spacing="16px" mb="20px">
                            <ListItem
                                display="flex"
                                alignItems="center"
                                gap="6px"
                            >
                                <ListIcon as={CheckmarkCircleIcon} />
                                <Text size="sm" textAlign="left">
                                    Entered the correct email/username and
                                    password
                                </Text>
                            </ListItem>
                            <ListItem
                                display="flex"
                                alignItems="center"
                                gap="6px"
                            >
                                <ListIcon as={CheckmarkCircleIcon} />
                                <Text size="sm" textAlign="left">
                                    Accepted any new TOS on your sportsbook
                                </Text>
                            </ListItem>
                            <ListItem
                                display="flex"
                                alignItems="center"
                                gap="6px"
                            >
                                <ListIcon as={CheckmarkCircleIcon} />
                                <Text size="sm" textAlign="left">
                                    Have completed any additional identity
                                    verification need on your sportsbook
                                </Text>
                            </ListItem>
                        </List>
                        <Text size="sm" mb="20px">
                            If the issue persists, submit your email below and
                            someone will reach out to troubleshoot
                        </Text>
                        <Heading textAlign="center" mb="20px">
                            Create Support Ticket
                        </Heading>
                        <Input
                            placeholder="Name"
                            mb="30px"
                            size="sm"
                            value={name}
                            onChange={handleChangeNameInput}
                        />
                        <Input
                            placeholder="Email"
                            type="email"
                            size="sm"
                            value={email}
                            onChange={handleChangeEmailInput}
                        />
                    </ModalBody>

                    <ModalFooter>
                        <Button
                            isLoading={isLoading}
                            onClick={handleSubmitButtonClick}
                        >
                            Create Support Ticket
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </form>
        </Modal>
    );
}
